import { useMemo, useState, useEffect} from 'react';
import { AGENCY_CONTRACT, PZH_CONTRACT } from './contractTypes';
import { useContractsByCustomerId } from './contractsSlice';
import axios from 'axios';
import moment from 'moment';
import { DATE_FORMAT } from '../date/utils';
//import url from 'socket.io-client/lib/url';
//import { query } from 'winston';


export const useContractsWithTypeByCustomerId = (customerId, type) => {
    const [contracts, { loading, initialized }] = useContractsByCustomerId(customerId);

    const forType = useMemo(
        () => (contracts ? contracts.filter((contract) => contract.type === type) : []),
        [contracts, type]
    );

    return [forType, { loading, initialized }];
};

export const usePzHContractByCustomerId = (customerId) => {
    const [[contract], { loading, initialized }] = useContractsWithTypeByCustomerId(
        customerId,
        PZH_CONTRACT
    );
    return [contract, { loading, initialized }];
};

export const useAgencyContractsByCustomerId = (customerId) =>
    useContractsWithTypeByCustomerId(customerId, AGENCY_CONTRACT);

/**
 * Whether a new BK-Einsatz may be created on this contract
 * (docs/plans/care-need-single-agency.md, rule 3): a complete agency contract,
 * not an addendum, whose end hasn't passed yet - the end day itself still counts.
 * Mirrored in the backend by CustomerContract::isActiveAgencyContract().
 */
export const isActiveAgencyContract = (contract) =>
    !!contract &&
    contract.type === AGENCY_CONTRACT &&
    !contract.parentContractId &&
    contract.completeness === 'complete' &&
    (!contract.endAt || !moment(contract.endAt, DATE_FORMAT).isBefore(moment().startOf('day')));

const fetchInvoicesByContractId = async (contractId) => {
    const response = await axios.get(`/api/v2/contracts/${contractId}/invoices`);
    return response.data;
};

export const useInvoicesByContracts = (contracts) => {
    const [invoices, setInvoices] = useState({});

    useEffect(() => {
        const fetchInvoices = async () => {
            const invoicesByContract = {};
            await Promise.all(
                contracts.map(async (contract) => {
                    const contractInvoices = await fetchInvoicesByContractId(contract.id);
                    invoicesByContract[contract.id] = contractInvoices;
                })
            );
            setInvoices(invoicesByContract);
        };

        if (contracts.length > 0) {
            fetchInvoices();
        }
    }, [contracts]);

    return invoices;
};

export const requestDocuSignEsignUrl = (data) =>
    axios({
        method: 'post',
        url: '/api/v3/docusign/esignurl',
        baseURL: process.env.REACT_APP_API_ROOT,
        data: data,
    })
    .then((response) => {
        return response.data;
    })
    .catch((error) => {
        if (error.response && error.response.data) {
            throw error.response.data;
        }
        throw error;
    });

//crm.pzh.test/api/v3/datev/syncDebitorData?client_number=10004&fiscal_years=&customer_id=32634
export const syncCustomerDataWithDatev = async ({clientNumber, customerId}) => {
    try {
        const response = await axios({
            method: 'get',
            url: '/api/v3/datev/syncDebitorData?client_number='+clientNumber+'&customer_id='+customerId,
            baseURL: process.env.REACT_APP_API_ROOT,
        });

        if (response.status === 204) {
            console.log("Warning: Data already exists for this customer in Datev.");
        }
        return response.data;
    } catch (error) {
        if (error.response) {
            if (error.response.status === 400) {
                console.log('syncCustomerDataWithDatev: Bad Request', error.response.data);
                // Only return the message, do not throw
                return { message: error.response.data?.error || 'Bad Request' };
            }
            throw error.response.data;
        }
        throw error;
    }
}
